import type { Document, MonthlyGroup } from '@/types'

/**
 * Groups documents by month (YYYY-MM format) and calculates totals.
 * Returns groups sorted by date descending (newest first).
 */
export function groupDocumentsByMonth(documents: Document[]): MonthlyGroup[] {
  const groups = new Map<string, Document[]>()

  // Group documents by month
  for (const doc of documents) {
    // Use document_date if available, otherwise fall back to created_at
    const dateStr = doc.document_date || doc.created_at || new Date().toISOString()
    const month = dateStr.substring(0, 7) // YYYY-MM
    if (!groups.has(month)) {
      groups.set(month, [])
    }
    groups.get(month)!.push(doc)
  }

  // Convert to MonthlyGroup array with calculated totals
  const result: MonthlyGroup[] = Array.from(groups.entries()).map(([month, docs]) => ({
    month,
    documents: docs.sort((a, b) => {
      const dateA = a.document_date || a.created_at || ''
      const dateB = b.document_date || b.created_at || ''
      return new Date(dateB).getTime() - new Date(dateA).getTime()
    }),
    totalValue: docs.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
    totalVat: docs.reduce((sum, d) => sum + (Number(d.vat_value) || 0), 0),
    count: docs.length,
    isExpanded: true,
  }))

  // Sort groups by month descending (newest first)
  return result.sort((a, b) => b.month.localeCompare(a.month))
}
